import { useState, useEffect } from "react";
import {
  getCallStatus,
  formatCallTime,
  endCall,
  deleteDailyRoom,
  getPastCalls,
  createDailyRoom,
  finalizeCall,
  notifyCallParticipants,
  updateCallSchedule,
  updateCallRecordingUrl,
  deleteCall,
} from "./calls";
import { setRecurringCallConfig } from "./recurringCalls";
import { getDoc, doc, getDocs, query, collection, where } from "firebase/firestore";
import { db } from "./firebase";

const card = {
  background: "#f5faff",
  borderRadius: "22px",
  padding: "20px",
  border: "2px solid #dbeafe",
  boxShadow: "0 8px 20px rgba(30,58,138,0.10)",
  marginBottom: "16px",
};

function useCountdown(targetDate) {
  const [remaining, setRemaining] = useState(null);
  useEffect(() => {
    if (!targetDate) return;
    const t = targetDate?.toDate ? targetDate.toDate() : new Date(targetDate);
    const tick = () => {
      const diff = t - new Date();
      if (diff <= 0) { setRemaining(null); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate]);
  return remaining;
}

export default function CallsScreen({ currentUser, groupCall, pollBroadcast, onCallVoted, isAdmin, onInCallChange }) {
  const [inCall, setInCall] = useState(false);
  const [callDisplayName, setCallDisplayName] = useState("");
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [pastCalls, setPastCalls] = useState([]);
  const [showPast, setShowPast] = useState(false);
  const [finalizingTime, setFinalizingTime] = useState(null);
  const [finalizeStatus, setFinalizeStatus] = useState("");
  const [endingCall, setEndingCall] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editStart, setEditStart] = useState("");
  const [editEnd, setEditEnd] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editingRecordingId, setEditingRecordingId] = useState(null);
  const [recordingUrlDraft, setRecordingUrlDraft] = useState("");
  const [savingRecording, setSavingRecording] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingCallId, setDeletingCallId] = useState(null);
  const [showMakeRecurring, setShowMakeRecurring] = useState(false);
  const [recurringTitle, setRecurringTitle] = useState("");
  const [recurringSaving, setRecurringSaving] = useState(false);
  const [recurringDone, setRecurringDone] = useState(false);

  const status = getCallStatus(groupCall);
  const countdown = useCountdown(groupCall?.scheduledTime);

  // Sync inCall up to App so the nav hides/shows reliably
  useEffect(() => {
    onInCallChange?.(inCall);
  }, [inCall]);

  // Auto-leave when admin ends the call externally
  useEffect(() => {
    if (inCall && (!groupCall || getCallStatus(groupCall) === "ended")) {
      setInCall(false);
    }
  }, [groupCall]);

  useEffect(() => {
    if (!showPast) return;
    getPastCalls(currentUser?.programTag || "", isAdmin ? currentUser?.uid : null, currentUser?.uid).then(setPastCalls);
  }, [showPast, currentUser?.programTag, currentUser?.uid, isAdmin]);

  // Tally poll votes from responses prop
  const voteTally = {};
  if (pollBroadcast?.options) {
    for (const opt of pollBroadcast.options) voteTally[opt] = 0;
  }
  if (pollBroadcast?.responses) {
    for (const r of pollBroadcast.responses) {
      for (const sel of r.selections || []) {
        voteTally[sel] = (voteTally[sel] || 0) + 1;
      }
    }
  }
  const userHasVoted = pollBroadcast?.responses?.some((r) => r.userId === currentUser?.uid);
  const totalVotes = pollBroadcast?.responses?.length || 0;

  async function handleAdminFinalize(option) {
    setFinalizingTime(option);
    setFinalizeStatus("Creating room...");
    try {
      const { roomUrl, roomName } = await createDailyRoom(groupCall.id, option);
      await finalizeCall(groupCall.id, { scheduledTime: option, roomUrl, roomName, winnerOption: option });
      await notifyCallParticipants(groupCall, option);
      setFinalizeStatus("Done! Call scheduled and users notified.");
    } catch (e) {
      setFinalizeStatus("Error: " + e.message);
    }
    setFinalizingTime(null);
  }

  function startEditing() {
    const t = groupCall?.scheduledTime?.toDate ? groupCall.scheduledTime.toDate() : groupCall?.scheduledTime ? new Date(groupCall.scheduledTime) : null;
    const e = groupCall?.scheduledEndTime?.toDate ? groupCall.scheduledEndTime.toDate() : groupCall?.scheduledEndTime ? new Date(groupCall.scheduledEndTime) : null;
    const toLocal = (d) => d ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "";
    setEditStart(toLocal(t));
    setEditEnd(toLocal(e));
    setEditing(true);
  }

  async function handleSaveEdit() {
    if (!editStart) return;
    setEditSaving(true);
    try {
      await updateCallSchedule(groupCall.id, {
        scheduledTime: new Date(editStart).toISOString(),
        scheduledEndTime: editEnd ? new Date(editEnd).toISOString() : null,
      });
      setEditing(false);
    } catch (e) {
      alert("Error saving: " + e.message);
    }
    setEditSaving(false);
  }

  function enterCall(displayName) {
    setCallDisplayName(displayName);
    setInCall(true);
  }

  function leaveCall() {
    setInCall(false);
  }

  async function handleEndCall() {
    setEndingCall(true);
    try { await deleteDailyRoom(groupCall.roomName); } catch (_) {}
    await endCall(groupCall.id);
    setEndingCall(false);
    leaveCall();
  }

  function handleJoinClick() {
    const defaultName = currentUser?.name?.trim() || "";
    setCallDisplayName(defaultName);
    setShowNamePrompt(true);
  }

  // ── Full-screen Daily.co call ───────────────────────────────────────────
  if (inCall && groupCall?.roomUrl) {
    const roomSrc = groupCall.roomUrl + (callDisplayName ? "?name=" + encodeURIComponent(callDisplayName) : "");
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 1100, background: "#000", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 16px", background: "#0f172a" }}>
          <span style={{ color: "#fff", fontWeight: "bold", fontSize: "15px" }}>📹 {groupCall.title || "Group Call"}</span>
          <div style={{ display: "flex", gap: "10px" }}>
            {isAdmin && (
              <button
                onClick={handleEndCall}
                disabled={endingCall}
                style={{ padding: "6px 14px", borderRadius: "10px", background: "#dc2626", color: "#fff", border: "none", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}
              >
                {endingCall ? "Ending..." : "End for All"}
              </button>
            )}
            <button
              onClick={leaveCall}
              style={{ padding: "6px 14px", borderRadius: "10px", background: "#475569", color: "#fff", border: "none", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}
            >
              Leave
            </button>
          </div>
        </div>
        <iframe
          src={roomSrc}
          allow="camera; microphone; screen-capture; fullscreen; display-capture"
          style={{ flex: 1, border: "none", width: "100%" }}
          title="Group Call"
        />
      </div>
    );
  }

  // ── Name prompt before joining ─────────────────────────────────────────
  if (showNamePrompt) {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 1100, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
        <div style={{ background: "#fff", borderRadius: "20px", padding: "28px 24px", width: "100%", maxWidth: "360px", boxShadow: "0 8px 32px rgba(0,0,0,0.25)" }}>
          <div style={{ fontWeight: "bold", fontSize: "18px", color: "#1e3a5f", marginBottom: "6px" }}>Join Call</div>
          <div style={{ fontSize: "13px", color: "#4b6b8a", marginBottom: "18px" }}>Your name will be visible to everyone in the call.</div>
          <input
            type="text"
            value={callDisplayName}
            onChange={(e) => setCallDisplayName(e.target.value)}
            placeholder="Your name"
            autoFocus
            style={{ width: "100%", padding: "12px 14px", borderRadius: "12px", border: "2px solid #bfdbfe", fontSize: "15px", color: "#1e3a5f", outline: "none", boxSizing: "border-box", marginBottom: "16px" }}
          />
          <button
            onClick={() => { setShowNamePrompt(false); enterCall(callDisplayName.trim() || currentUser?.name || ""); }}
            disabled={!callDisplayName.trim()}
            style={{ width: "100%", padding: "13px", borderRadius: "13px", background: callDisplayName.trim() ? "#2563eb" : "#bfdbfe", color: "#fff", border: "none", fontWeight: "bold", fontSize: "16px", cursor: callDisplayName.trim() ? "pointer" : "default", marginBottom: "10px" }}
          >
            Join Now →
          </button>
          <button
            onClick={() => setShowNamePrompt(false)}
            style={{ width: "100%", padding: "10px", borderRadius: "12px", background: "transparent", color: "#64748b", border: "2px solid #e2e8f0", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Trebuchet MS','Segoe UI',Tahoma,Geneva,Verdana,sans-serif" }}>

      {/* ── LIVE ─────────────────────────────────────────────────── */}
      {status === "live" && (
        <div style={{ ...card, background: "#ecfdf5", border: "2px solid #6ee7b7", textAlign: "center" }}>
          <div style={{ fontSize: "36px", marginBottom: "8px" }}>🟢</div>
          <div style={{ fontWeight: "bold", color: "#065f46", fontSize: "20px", marginBottom: "4px" }}>Call is live now!</div>
          <div style={{ color: "#047857", fontSize: "14px", marginBottom: "16px" }}>{groupCall.title}</div>
          <button
            onClick={handleJoinClick}
            style={{ width: "100%", padding: "14px", borderRadius: "14px", background: "#059669", color: "#fff", border: "none", fontWeight: "bold", fontSize: "17px", cursor: "pointer", boxShadow: "0 4px 14px rgba(5,150,105,0.35)" }}
          >
            Join Now →
          </button>
          {isAdmin && (
            <button
              onClick={handleEndCall}
              disabled={endingCall}
              style={{ marginTop: "10px", width: "100%", padding: "10px", borderRadius: "12px", background: "transparent", color: "#dc2626", border: "2px solid #fca5a5", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}
            >
              {endingCall ? "Ending call..." : "End Call for Everyone"}
            </button>
          )}
        </div>
      )}

      {/* ── SCHEDULED ─────────────────────────────────────────────── */}
      {status === "scheduled" && (
        <div style={{ ...card, textAlign: "center" }}>
          <div style={{ fontSize: "32px", marginBottom: "8px" }}>📅</div>
          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "19px", marginBottom: "4px" }}>{groupCall.title}</div>

          {editing ? (
            <div style={{ textAlign: "left", display: "grid", gap: "10px", marginBottom: "12px" }}>
              <div>
                <div style={{ fontSize: "12px", fontWeight: "bold", color: "#4b6b8a", marginBottom: "4px" }}>Start time</div>
                <input type="datetime-local" value={editStart} onChange={(e) => setEditStart(e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: "10px", border: "2px solid #bfdbfe", fontSize: "14px", boxSizing: "border-box" }} />
              </div>
              <div>
                <div style={{ fontSize: "12px", fontWeight: "bold", color: "#4b6b8a", marginBottom: "4px" }}>End time (join window closes)</div>
                <input type="datetime-local" value={editEnd} onChange={(e) => setEditEnd(e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: "10px", border: "2px solid #bfdbfe", fontSize: "14px", boxSizing: "border-box" }} />
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button onClick={handleSaveEdit} disabled={editSaving || !editStart} style={{ flex: 1, padding: "9px", borderRadius: "10px", background: "#2563eb", color: "#fff", border: "none", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}>{editSaving ? "Saving..." : "Save"}</button>
                <button onClick={() => setEditing(false)} style={{ flex: 1, padding: "9px", borderRadius: "10px", background: "transparent", border: "2px solid #dbeafe", color: "#4b6b8a", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ color: "#2563eb", fontWeight: "bold", fontSize: "16px", marginBottom: "4px" }}>
                {formatCallTime(groupCall.scheduledTime)}
              </div>
              {groupCall.scheduledEndTime && (
                <div style={{ color: "#64748b", fontSize: "12px", marginBottom: "4px" }}>
                  Join window closes: {formatCallTime(groupCall.scheduledEndTime)}
                </div>
              )}
              {countdown && (
                <div style={{ color: "#4b6b8a", fontSize: "13px", marginBottom: "12px" }}>Starts in {countdown}</div>
              )}
              {isAdmin && (
                <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginBottom: "12px", flexWrap: "wrap" }}>
                  <button onClick={startEditing} style={{ padding: "6px 14px", borderRadius: "10px", border: "2px solid #bfdbfe", background: "transparent", color: "#2563eb", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>✏️ Edit time</button>
                  {!recurringDone && (
                    <button
                      onClick={() => {
                        setRecurringTitle(groupCall.title || "Weekly Group Call");
                        setShowMakeRecurring(true);
                      }}
                      style={{ padding: "6px 14px", borderRadius: "10px", border: "2px solid #a5f3fc", background: "#f0fdfe", color: "#0e7490", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}
                    >
                      🔄 Make Recurring
                    </button>
                  )}
                  {recurringDone && (
                    <span style={{ padding: "6px 14px", borderRadius: "10px", background: "#dcfce7", color: "#166534", fontWeight: "bold", fontSize: "12px" }}>
                      ✓ Recurring set!
                    </span>
                  )}
                </div>
              )}

              {/* Make Recurring modal */}
              {showMakeRecurring && (() => {
                const t = groupCall.scheduledTime?.toDate ? groupCall.scheduledTime.toDate() : new Date(groupCall.scheduledTime);
                const dayName = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][t.getDay()];
                const timeStr = t.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

                async function handleConfirm() {
                  setRecurringSaving(true);
                  try {
                    await setRecurringCallConfig({
                      active: true,
                      title: recurringTitle.trim() || "Weekly Group Call",
                      dayOfWeek: t.getDay(),
                      hour: t.getHours(),
                      minute: t.getMinutes(),
                      timezoneLabel: timeStr,
                      programTag: groupCall.programTag || "",
                      targetType: groupCall.targetType || "program",
                      targetUserIds: groupCall.targetUserIds || [],
                      createdBy: groupCall.createdBy || "",
                    });
                    setShowMakeRecurring(false);
                    setRecurringDone(true);
                  } catch (e) {
                    alert("Error saving: " + e.message);
                  }
                  setRecurringSaving(false);
                }

                return (
                  <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
                    <div style={{ background: "#fff", borderRadius: "20px", padding: "24px", width: "100%", maxWidth: "380px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
                      <div style={{ fontWeight: "bold", fontSize: "17px", color: "#1e3a5f", marginBottom: "6px" }}>🔄 Make Recurring</div>
                      <div style={{ fontSize: "13px", color: "#4b6b8a", marginBottom: "16px" }}>
                        This will create a weekly call every <strong>{dayName} at {timeStr}</strong>. Clients will see it in the Calls tab and can RSVP each week.
                      </div>
                      <div style={{ marginBottom: "14px" }}>
                        <div style={{ fontSize: "12px", fontWeight: "bold", color: "#4b6b8a", marginBottom: "4px" }}>Call title</div>
                        <input
                          type="text"
                          value={recurringTitle}
                          onChange={(e) => setRecurringTitle(e.target.value)}
                          style={{ width: "100%", padding: "9px 12px", borderRadius: "10px", border: "2px solid #bfdbfe", fontSize: "14px", color: "#1e3a5f", outline: "none", boxSizing: "border-box" }}
                        />
                      </div>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <button onClick={handleConfirm} disabled={recurringSaving} style={{ flex: 1, padding: "11px", borderRadius: "11px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}>
                          {recurringSaving ? "Saving..." : "Set as Recurring →"}
                        </button>
                        <button onClick={() => setShowMakeRecurring(false)} style={{ padding: "11px 16px", borderRadius: "11px", border: "2px solid #dbeafe", background: "transparent", color: "#4b6b8a", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </>
          )}

          <div style={{ background: "#f0f9ff", borderRadius: "12px", padding: "12px 16px", border: "1px solid #bae6fd", marginBottom: "16px", fontSize: "13px", color: "#0c4a6e" }}>
            <strong>How to join:</strong> When it's time, tap the Join button that will appear here. You'll need to allow camera & microphone access.
          </div>
          <button
            disabled
            style={{ width: "100%", padding: "12px", borderRadius: "14px", background: "#bfdbfe", color: "#1e3a5f", border: "none", fontWeight: "bold", fontSize: "15px", cursor: "default" }}
          >
            Join Call (available at call time)
          </button>
        </div>
      )}

      {/* ── POLLING ───────────────────────────────────────────────── */}
      {status === "polling" && (
        <div style={card}>
          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "18px", marginBottom: "4px" }}>
            📊 {groupCall?.title || "Group Call Time Poll"}
          </div>
          <div style={{ color: "#4b6b8a", fontSize: "13px", marginBottom: "16px" }}>
            Vote for your preferred time. The most popular option wins!
          </div>

          {/* User vote UI — only shown if they haven't voted yet */}
          {!userHasVoted && pollBroadcast && (
            <div style={{ marginBottom: "16px" }}>
              <div style={{ color: "#1e3a5f", fontWeight: "bold", fontSize: "14px", marginBottom: "10px" }}>Pick your preferred time:</div>
              {(pollBroadcast.options || []).map((opt) => (
                <button
                  key={opt}
                  onClick={() => onCallVoted && onCallVoted(opt)}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 16px", borderRadius: "12px", border: "2px solid #bfdbfe", background: "#f8fbff", color: "#1e3a5f", fontWeight: "bold", fontSize: "14px", cursor: "pointer", marginBottom: "8px" }}
                >
                  {formatCallTime(opt)}
                </button>
              ))}
            </div>
          )}

          {/* Waiting state after voting */}
          {userHasVoted && !isAdmin && (
            <div style={{ background: "#eff6ff", borderRadius: "12px", padding: "14px 16px", border: "1px solid #bfdbfe", color: "#1e3a5f", fontSize: "14px", marginBottom: "16px" }}>
              ✅ Your vote is in! Waiting for everyone to respond...
            </div>
          )}

          {/* Vote tally — visible to admin always, to users after they've voted */}
          {(isAdmin || userHasVoted) && pollBroadcast?.options && (
            <div style={{ marginTop: "8px" }}>
              <div style={{ color: "#4b6b8a", fontSize: "12px", marginBottom: "8px" }}>
                {totalVotes} response{totalVotes !== 1 ? "s" : ""} so far
              </div>
              {(pollBroadcast.options || []).map((opt) => {
                const votes = voteTally[opt] || 0;
                const pct = totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;
                return (
                  <div key={opt} style={{ marginBottom: "10px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#1e3a5f", marginBottom: "4px" }}>
                      <span style={{ fontWeight: "bold" }}>{formatCallTime(opt)}</span>
                      <span>{votes} vote{votes !== 1 ? "s" : ""}</span>
                    </div>
                    <div style={{ height: "8px", borderRadius: "999px", background: "#dbeafe", overflow: "hidden" }}>
                      <div style={{ height: "100%", borderRadius: "999px", background: "#2563eb", width: `${pct}%`, transition: "width 0.3s" }} />
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => handleAdminFinalize(opt)}
                        disabled={!!finalizingTime}
                        style={{ marginTop: "6px", padding: "5px 12px", borderRadius: "8px", border: "1.5px solid #2563eb", background: finalizingTime === opt ? "#2563eb" : "transparent", color: finalizingTime === opt ? "#fff" : "#2563eb", fontSize: "12px", fontWeight: "bold", cursor: "pointer" }}
                      >
                        {finalizingTime === opt ? "Creating room..." : "Use this time"}
                      </button>
                    )}
                  </div>
                );
              })}
              {finalizeStatus && (
                <div style={{ marginTop: "8px", fontSize: "13px", color: finalizeStatus.startsWith("Error") ? "#dc2626" : "#059669", fontWeight: "bold" }}>
                  {finalizeStatus}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── NO CALL ───────────────────────────────────────────────── */}
      {!status && (
        <div style={{ ...card, textAlign: "center", padding: "32px 20px" }}>
          <div style={{ fontSize: "40px", marginBottom: "12px" }}>📞</div>
          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "18px", marginBottom: "8px" }}>No call scheduled yet</div>
          <div style={{ color: "#4b6b8a", fontSize: "14px" }}>Your coach will send a time poll when the next group call is coming up.</div>
        </div>
      )}

      {/* ── HOW TO JOIN INFO ──────────────────────────────────────── */}
      {(status === "scheduled" || status === "live") && (
        <div style={{ background: "#fefce8", borderRadius: "18px", padding: "16px 18px", border: "2px solid #fde68a", marginBottom: "16px" }}>
          <div style={{ fontWeight: "bold", color: "#92400e", fontSize: "14px", marginBottom: "6px" }}>📋 What you'll need</div>
          <ul style={{ margin: 0, paddingLeft: "18px", color: "#78350f", fontSize: "13px", lineHeight: 1.8 }}>
            <li>Allow camera and microphone when prompted</li>
            <li>Works on phone or computer</li>
            <li>Your coach can share their screen during the call</li>
            <li>Use the chat panel inside the call to ask questions</li>
          </ul>
        </div>
      )}

      {/* ── PAST CALLS ────────────────────────────────────────────── */}
      <button
        onClick={() => setShowPast((p) => !p)}
        style={{ width: "100%", padding: "10px", borderRadius: "12px", border: "2px solid #dbeafe", background: "transparent", color: "#4b6b8a", fontSize: "13px", fontWeight: "bold", cursor: "pointer", marginBottom: "8px" }}
      >
        {showPast ? "▲ Hide past calls" : "▼ Show past calls"}
      </button>
      {showPast && (
        <div>
          {pastCalls.length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: "13px", textAlign: "center", padding: "12px" }}>No past calls yet.</div>
          ) : (
            pastCalls.map((c) => (
              <div key={c.id} style={{ background: "#f8fbff", borderRadius: "14px", padding: "12px 16px", border: "1px solid #dbeafe", marginBottom: "8px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px" }}>{c.title}</div>
                    <div style={{ color: "#6b8cb0", fontSize: "12px" }}>{formatCallTime(c.scheduledTime)}</div>
                  </div>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    {c.recordingUrl && (
                      <a
                        href={c.recordingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: "12px", fontWeight: "bold", color: "#2563eb", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "8px", padding: "4px 10px", textDecoration: "none" }}
                      >
                        ▶ Recording
                      </a>
                    )}
                    {isAdmin && editingRecordingId !== c.id && (
                      <button
                        onClick={() => { setEditingRecordingId(c.id); setRecordingUrlDraft(c.recordingUrl || ""); }}
                        style={{ fontSize: "11px", color: "#6b7280", background: "transparent", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "4px 8px", cursor: "pointer" }}
                      >
                        {c.recordingUrl ? "Edit" : "+ Recording"}
                      </button>
                    )}
                    {!c.recordingUrl && !isAdmin && (
                      <span style={{ fontSize: "11px", color: "#94a3b8", background: "#f1f5f9", borderRadius: "8px", padding: "3px 8px" }}>Ended</span>
                    )}
                    {isAdmin && confirmDeleteId !== c.id && (
                      <button
                        onClick={() => setConfirmDeleteId(c.id)}
                        style={{ fontSize: "11px", color: "#dc2626", background: "transparent", border: "1px solid #fca5a5", borderRadius: "8px", padding: "4px 8px", cursor: "pointer" }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
                {isAdmin && confirmDeleteId === c.id && (
                  <div style={{ marginTop: "10px", display: "flex", alignItems: "center", gap: "8px", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "10px", padding: "8px 12px" }}>
                    <span style={{ fontSize: "13px", color: "#dc2626", fontWeight: "bold", flex: 1 }}>Delete this call for everyone?</span>
                    <button
                      disabled={deletingCallId === c.id}
                      onClick={async () => {
                        setDeletingCallId(c.id);
                        await deleteCall(c.id);
                        setPastCalls((prev) => prev.filter((x) => x.id !== c.id));
                        setConfirmDeleteId(null);
                        setDeletingCallId(null);
                      }}
                      style={{ padding: "5px 12px", borderRadius: "8px", border: "none", background: "#dc2626", color: "#fff", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}
                    >
                      {deletingCallId === c.id ? "Deleting..." : "Yes, delete"}
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      style={{ padding: "5px 10px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "transparent", color: "#6b7280", fontSize: "12px", cursor: "pointer" }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
                {isAdmin && editingRecordingId === c.id && (
                  <div style={{ marginTop: "10px", display: "flex", gap: "8px", alignItems: "center" }}>
                    <input
                      type="url"
                      value={recordingUrlDraft}
                      onChange={(e) => setRecordingUrlDraft(e.target.value)}
                      placeholder="Paste recording URL (Daily.co, YouTube, Drive...)"
                      style={{ flex: 1, padding: "7px 10px", borderRadius: "10px", border: "2px solid #bfdbfe", fontSize: "13px", color: "#1e3a5f", outline: "none" }}
                    />
                    <button
                      disabled={savingRecording}
                      onClick={async () => {
                        setSavingRecording(true);
                        await updateCallRecordingUrl(c.id, recordingUrlDraft.trim());
                        setPastCalls((prev) => prev.map((x) => x.id === c.id ? { ...x, recordingUrl: recordingUrlDraft.trim() || null } : x));
                        setEditingRecordingId(null);
                        setSavingRecording(false);
                      }}
                      style={{ padding: "7px 14px", borderRadius: "10px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", fontSize: "13px", cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      {savingRecording ? "Saving..." : "Save"}
                    </button>
                    <button
                      onClick={() => setEditingRecordingId(null)}
                      style={{ padding: "7px 10px", borderRadius: "10px", border: "1px solid #e2e8f0", background: "transparent", color: "#6b7280", fontSize: "13px", cursor: "pointer" }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
